import { useEffect, useRef } from 'react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  HttpTransportType,
} from '@microsoft/signalr';

/**
 * Hook de conexão SignalR com suporte a grupos e reconexão estável.
 *
 * @param {string} hubUrl - Caminho do hub (ex: "/hub/alertas")
 * @param {Object} options - Objeto memoizado com os parâmetros da conexão
 * @param {string[]} options.eventos - Lista de eventos SignalR a escutar
 * @param {function} options.onEvent - Callback para evento recebido
 * @param {string[]} [options.grupos=[]] - Grupos SignalR (ex: ["web_abc"])
 * @param {function} [options.onConnection] - Callback ao conectar
 * @param {number} [options.reconnectDelay=5000] - Delay inicial de reconexão
 */
const useSignalR = (hubUrl, options) => {
  const connectionRef = useRef(null);

  useEffect(() => {
    if (!hubUrl || !options) return;

    const {
      eventos,
      onEvent,
      grupos = [],
      onConnection,
      reconnectDelay = 5000,
    } = options;

    if (!eventos || !onEvent) {
      console.warn('[SignalR] ⚠️ "eventos" ou "onEvent" ausentes.');
      return;
    }

    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_SIGNALR_BASE_URL?.replace(/\/+$/, '');

    if (!baseUrl) {
      console.warn('[SignalR] ❌ Variável REACT_APP_SIGNALR_BASE_URL ausente.');
      return;
    }

    const isPublic = window.location.pathname.includes('/evento');

    if (!token && !isPublic) {
      console.warn('[SignalR] ❌ Token ausente e não é rota pública.');
      return;
    }

    const fullHubUrl = `${baseUrl}${hubUrl}`;
    const connIdPreview = () =>
      connectionRef.current?.connectionId?.slice?.(0, 8) || '???';

    const connection = new HubConnectionBuilder()
      .withUrl(fullHubUrl, {
        accessTokenFactory: isPublic ? () => null : () => token,
        transport: HttpTransportType.WebSockets,
        withCredentials: true,
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) =>
          Math.min(1000 * 2 ** ctx.previousRetryCount, 30000),
      })
      .build();

    connectionRef.current = connection;

    const startConnection = async () => {
      try {
        await connection.start();
        console.log(`[SignalR] ✅ Conectado | ConnID: ${connIdPreview()}`);

        for (const grupo of grupos) {
          try {
            await connection.invoke('JoinEvento', grupo);
            console.log(`📡 Entrou no grupo: ${grupo} | ConnID: ${connIdPreview()}`);
          } catch (err) {
            console.error(`❌ Falha ao entrar no grupo ${grupo}`, err);
          }
        }

        if (typeof onConnection === 'function') onConnection(connection);

        connection.onreconnecting((err) => {
          console.warn('🔄 Reconectando...', err);
        });

        connection.onreconnected(async (id) => {
          console.log(`🔁 Reconectado | Novo ConnID: ${id}`);
          for (const grupo of grupos) {
            try {
              await connection.invoke('JoinEvento', grupo);
              console.log(`📡 Reentrou no grupo: ${grupo}`);
            } catch (err) {
              console.error(`❌ Falha ao reentrar no grupo ${grupo}`, err);
            }
          }
        });

        connection.onclose((err) => {
          console.warn('🔌 Conexão encerrada:', err);
        });

        eventos.forEach((eventName) => {
          connection.on(eventName, (data) => {
            console.log(`📥 Evento: ${eventName}`, data);
            onEvent(eventName, data);
          });
        });
      } catch (err) {
        console.error('❌ Erro ao conectar:', err);
        setTimeout(startConnection, reconnectDelay);
      }
    };

    startConnection();

    return () => {
      eventos.forEach((eventName) => connection.off(eventName));
      if (connection.state === HubConnectionState.Connected) {
        connection
          .stop()
          .then(() => console.log('🛑 Desconectado do SignalR'))
          .catch((err) => console.error('❌ Erro ao desconectar', err));
      } else {
        console.log('⚠️ Conexão já encerrada');
      }
    };
  }, [hubUrl, options]); // options deve ser memoizado com useMemo
};

export default useSignalR;
